import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from '../lib/cn.js';
/**
 * Tooltip, Menu, Segmented, Callout, Disclosure.
 *
 * Modals deliberately live in `modal.tsx`, not here. Two competing modal primitives is worse than
 * the eleven hand-rolled overlays they replaced, so the Tab focus trap this file originally carried
 * was contributed into that component instead.
 */
// ---------------------------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------------------------
/**
 * A hover/focus tooltip.
 *
 * CSS-only reveal via group-hover plus focus-within, so it needs no positioning library and no
 * state. It is deliberately NOT a replacement for a `title` attribute on genuinely optional
 * information: this renders visually only, so anything essential must also be in the accessible
 * name of the trigger.
 */
export function Tooltip({ label, children, side = 'top', }) {
    return (_jsxs("span", { className: "group/tt relative inline-flex", children: [children, _jsx("span", { role: "tooltip", className: cn('pointer-events-none absolute left-1/2 z-40 hidden -translate-x-1/2 whitespace-nowrap rounded-sm', 'border border-border-default bg-bg-overlay px-2 py-1 text-micro text-text-primary shadow-md', 'group-hover/tt:block group-focus-within/tt:block', side === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'), children: label })] }));
}
// ---------------------------------------------------------------------------------------------
// Segmented
// ---------------------------------------------------------------------------------------------
/** A small exclusive choice. Previously implemented twice, independently, with different markup. */
export function Segmented({ options, value, onChange, size = 'md', label, className, }) {
    return (_jsx("div", { role: "group", "aria-label": label, className: cn('inline-flex rounded-sm border border-border-default bg-bg-inset p-0.5', className), children: options.map((o) => {
            const on = o.value === value;
            return (_jsx("button", { type: "button", "aria-pressed": on, onClick: () => onChange(o.value), className: cn('rounded-[3px] font-medium transition-colors duration-fast motion-reduce:transition-none', size === 'sm' ? 'px-2 py-0.5 text-micro' : 'px-2.5 py-1 text-xs', on ? 'bg-bg-raised text-text-primary shadow-sm' : 'text-text-muted hover:text-text-primary'), children: o.label }, o.value));
        }) }));
}
// ---------------------------------------------------------------------------------------------
// Menu
// ---------------------------------------------------------------------------------------------
/**
 * An overflow menu.
 *
 * Exists so a toolbar can show the two or three things that matter and put the rest behind one
 * control, instead of the current report header's flat row of thirteen buttons, eleven of which are
 * the same grey. A row of equally-weighted controls tells the reader nothing about what to do.
 *
 * Closes on outside pointer-down, on Escape, and on activating any item inside it. Escape is bound
 * to the container rather than the document so a menu inside a Dialog does not close both at once.
 */
/** Lets a MenuItem dismiss its own menu without the container listening for bubbled clicks. */
const MenuCloseContext = React.createContext(() => { });
export function Menu({ label, children, align = 'right', }) {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef(null);
    React.useEffect(() => {
        if (!open)
            return;
        const onDown = (e) => {
            if (!ref.current?.contains(e.target))
                setOpen(false);
        };
        // Escape is handled here rather than with onKeyDown on the wrapper: the wrapper is a positioning
        // div with no role, and hanging a key handler on it both trips
        // jsx-a11y/no-static-element-interactions and misses the case where focus has already left.
        const onKey = (e) => {
            if (e.key === 'Escape')
                setOpen(false);
        };
        document.addEventListener('pointerdown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('pointerdown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    const close = React.useCallback(() => setOpen(false), []);
    return (_jsxs("div", { ref: ref, className: "relative", children: [_jsxs("button", { type: "button", "aria-haspopup": "menu", "aria-expanded": open, onClick: () => setOpen((v) => !v), className: cn('inline-flex h-8 items-center gap-1.5 rounded-md border border-border-default bg-bg-overlay px-3 text-sm font-medium text-text-primary', 'transition-colors duration-fast hover:border-border-strong motion-reduce:transition-none', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base'), children: [label, _jsx("span", { "aria-hidden": true, className: "text-micro text-text-muted", children: "\u25BE" })] }), open && (_jsx("div", { role: "menu", className: cn('absolute z-40 mt-1 min-w-[13rem] overflow-hidden rounded-md border border-border-default bg-bg-overlay p-1 shadow-md animate-rise-in', align === 'right' ? 'right-0' : 'left-0'), children: _jsx(MenuCloseContext.Provider, { value: close, children: children }) }))] }));
}
/** One row in a Menu. Renders as a button unless given an `href`/`to` child element. */
export function MenuItem({ children, onSelect, tone = 'neutral', disabled, }) {
    const close = React.useContext(MenuCloseContext);
    return (_jsx("button", { type: "button", role: "menuitem", disabled: disabled, onClick: () => {
            onSelect?.();
            close();
        }, className: cn('block w-full rounded-sm px-2.5 py-1.5 text-left text-sm transition-colors duration-fast motion-reduce:transition-none', 'disabled:pointer-events-none disabled:opacity-50', tone === 'danger'
            ? 'text-status-danger hover:bg-status-danger/10'
            : 'text-text-secondary hover:bg-bg-raised hover:text-text-primary'), children: children }));
}
/** A non-interactive wrapper so links and custom controls sit on the menu's row rhythm. */
export function MenuRow({ children }) {
    return (_jsx("div", { role: "menuitem", className: "block rounded-sm px-2.5 py-1.5 text-sm text-text-secondary transition-colors duration-fast hover:bg-bg-raised hover:text-text-primary motion-reduce:transition-none [&_a]:block [&_button]:w-full [&_button]:text-left", children: children }));
}
export function MenuSeparator() {
    return _jsx("div", { role: "separator", className: "my-1 border-t border-border-subtle" });
}
// ---------------------------------------------------------------------------------------------
// Callout
// ---------------------------------------------------------------------------------------------
const CALLOUT_TONE = {
    info: 'border-accent-500/30 bg-accent-500/5 text-text-secondary',
    warning: 'border-status-warning/30 bg-status-warning/5 text-text-secondary',
    danger: 'border-status-danger/30 bg-status-danger/5 text-text-secondary',
    success: 'border-verdict-clean/30 bg-verdict-clean/5 text-text-secondary',
};
const CALLOUT_MARK = {
    info: 'text-accent-500',
    warning: 'text-status-warning',
    danger: 'text-status-danger',
    success: 'text-verdict-clean',
};
// Spelled out rather than derived from CALLOUT_MARK with a string replace. Tailwind scans source
// statically, so a class assembled at runtime is never generated and the element silently renders
// with no colour at all. `dead-class-guard` catches exactly this.
const CALLOUT_DOT = {
    info: 'bg-accent-500',
    warning: 'bg-status-warning',
    danger: 'bg-status-danger',
    success: 'bg-verdict-clean',
};
/**
 * A short status statement with a severity.
 *
 * Built for the degraded-analysis notices, which are currently five lines of undifferentiated grey
 * prose where "the sandbox was busy, a retry is queued" looks exactly like "this file is corrupt and
 * can never run". A headline carries the fact, the tone carries the severity, and anything longer
 * goes behind `details` instead of into the paragraph.
 */
export function Callout({ tone = 'info', title, children, details, action, className, }) {
    return (_jsx("div", { className: cn('rounded-sm border px-3 py-2.5', CALLOUT_TONE[tone], className), children: _jsxs("div", { className: "flex items-start gap-2", children: [_jsx("span", { "aria-hidden": true, className: cn('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', CALLOUT_DOT[tone]) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: cn('text-sm font-semibold', CALLOUT_MARK[tone]), children: title }), children && _jsx("div", { className: "mt-1 text-sm leading-relaxed", children: children }), details && (_jsxs("details", { className: "mt-1.5", children: [_jsx("summary", { className: "cursor-pointer text-micro text-text-muted hover:text-text-secondary", children: "Details" }), _jsx("div", { className: "mt-1.5 text-sm leading-relaxed text-text-muted", children: details })] })), action && _jsx("div", { className: "mt-2", children: action })] })] }) }));
}
// ---------------------------------------------------------------------------------------------
// Disclosure
// ---------------------------------------------------------------------------------------------
/** An expandable section with a real button and honest aria state. */
export function Disclosure({ summary, count, defaultOpen = false, children, }) {
    const [open, setOpen] = React.useState(defaultOpen);
    const id = React.useId();
    return (_jsxs("div", { children: [_jsxs("button", { type: "button", "aria-expanded": open, "aria-controls": id, onClick: () => setOpen((v) => !v), className: "flex w-full items-center gap-2 py-1.5 text-left text-sm font-medium text-text-secondary transition-colors duration-fast hover:text-text-primary motion-reduce:transition-none", children: [_jsx("span", { "aria-hidden": true, className: cn('text-micro text-text-muted transition-transform duration-fast', open && 'rotate-90'), children: "\u25B8" }), _jsx("span", { className: "min-w-0 flex-1 truncate", children: summary }), count !== undefined && _jsx("span", { className: "shrink-0 font-mono text-micro text-text-muted", children: count })] }), open && (_jsx("div", { id: id, className: "animate-fade-in", children: children }))] }));
}
//# sourceMappingURL=overlay.js.map